import React from 'react'
import { createTheme, responsiveFontSizes } from '@mui/material/styles'
import { RoundedCheckboxBlank, RoundedCheckboxChecked, RoundedCheckboxIndeterminate } from './components/system/RoundedCheckboxIcons'

// Platinum brand palette
const platinum = {
  navy: '#263757',
  navyDark: '#0A1628',
  navyLight: '#243352',
  gold: '#FFC000',
  goldLight: '#FFD04A',
  slate: {
    500: '#64748B',
    300: '#CBD5E1',
    200: '#E2E8F0',
    100: '#F1F5F9',
    50: '#F8FAFC',
  },
}

const theme = responsiveFontSizes(createTheme({
  palette: {
    primary: {
      main: platinum.navy,
      light: platinum.navyLight,
      dark: platinum.navyDark,
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: platinum.gold,
      light: platinum.goldLight,
      dark: '#E6AD00',
      contrastText: platinum.navyDark,
    },
    info: {
      main: '#3B82F6',
      light: '#60A5FA',
      dark: '#2563EB',
    },
    background: {
      default: platinum.slate[50],
      paper: '#FFFFFF',
    },
    text: {
      primary: platinum.navyDark,
      secondary: platinum.slate[500],
    },
    divider: 'rgba(0, 0, 0, 0.06)',
    action: {
      hover: 'rgba(17, 29, 53, 0.04)',
      selected: 'rgba(17, 29, 53, 0.08)',
    },
  },
  typography: {
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    h5: {
      fontWeight: 600,
      letterSpacing: '-0.01em',
    },
    h6: {
      fontWeight: 600,
    },
    button: {
      textTransform: 'none' as const,
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 6,
          fontWeight: 500,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontSize: '0.75rem',
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          fontSize: '0.75rem',
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: '0.75rem',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRadius: 0,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: 0,
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.info.main,
            borderWidth: '2px',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(0, 0, 0, 0.45)',
          },
        }),
        notchedOutline: {
          borderColor: 'rgba(0, 0, 0, 0.23)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 0,
        },
      },
    },
    MuiCheckbox: {
      // Slightly rounded boxes everywhere — see RoundedCheckboxIcons.
      defaultProps: {
        icon: <RoundedCheckboxBlank />,
        checkedIcon: <RoundedCheckboxChecked />,
        indeterminateIcon: <RoundedCheckboxIndeterminate />,
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          fontWeight: 500,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: platinum.navy,
          fontSize: '0.75rem',
          borderRadius: 4,
        },
        arrow: {
          color: platinum.navy,
        },
      },
    },
  },
}))

/**
 * The thickness of the gold rule under the app header (pages/Layout). The
 * Help pane's blue left edge is drawn at the same thickness (Adam, 14 Oct).
 */
export const HEADER_RULE_PX = 8

export { platinum }
export default theme
